import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Gavel, Loader2, RefreshCw, X } from "lucide-react";
import { useBuckets } from "./shared/useBuckets";
import { SettlementDrawer } from "./shared/SettlementDrawer";
import { settlementsApi } from "./api";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { StatusBadge } from "../applications/shared/StatusBadge";
import { formatApplicantName, formatDateTime } from "../applications/shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { fmtCLP } from "../dashboard/format";
import {
  DISPUTE_STATUS_LABELS,
  DISPUTE_TYPE_LABELS,
  SETTLEMENT_STATUS_LABELS,
  formatDateShort,
  settlementStatusTone,
} from "./shared/labels";
import type {
  DisputeRow,
  DisputeStatus,
  FullDetailResponse,
  SettlementDispute,
} from "./types";
import { useToast } from "@/app/providers/Toaster";

export function DisputesScreen() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const query = useBuckets();

  const rows = query.data?.buckets.disputasAbiertas ?? [];

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Liquidaciones · Disputas</p>
          <h1 className="nx-h1">Disputas abiertas</h1>
          <p className="text-sm text-fg-muted mt-1">
            Productores que abrieron disputa contra el cálculo. Mixcore actúa
            como árbitro: revisá evidencia, decidí resolución, ajustá monto si
            corresponde. La minuta queda en el audit log inmutable y se envía
            por email al productor.
          </p>
        </div>
        <button
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="nx-btn-secondary"
        >
          <RefreshCw size={14} className={query.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      {query.isLoading ? (
        <LoadingBlock label="Cargando disputas…" />
      ) : query.isError ? (
        <ErrorBlock onRetry={() => query.refetch()} />
      ) : (
        <ApplicationsTable<DisputeRow & { id: number }>
          rows={rows.map((r) => ({ ...r, id: r.settlementId }))}
          columns={columns}
          page={1}
          pageSize={rows.length || 1}
          total={rows.length}
          onPageChange={() => {}}
          onRowClick={(r) => setSelectedId(r.settlementId)}
          selectedId={selectedId}
          emptyLabel="No hay disputas abiertas. 🎉"
        />
      )}

      <SettlementDrawer
        settlementId={selectedId}
        onClose={() => {
          setSelectedId(null);
          setResolveOpen(false);
        }}
        renderActions={(data) => {
          const openDispute = data.disputes.find(
            (d) =>
              d.statusDisputa === "abierta" || d.statusDisputa === "en_revision_mixcore",
          );
          if (!openDispute) {
            return (
              <span className="text-2xs text-fg-subtle italic">
                Sin disputas abiertas en esta liquidación.
              </span>
            );
          }
          return (
            <button onClick={() => setResolveOpen(true)} className="nx-btn-primary">
              <Gavel size={14} /> Resolver disputa #{openDispute.id}
            </button>
          );
        }}
      />

      {resolveOpen && selectedId !== null && (
        <ResolveDisputeModal
          settlementId={selectedId}
          onClose={() => setResolveOpen(false)}
          onResolved={() => {
            setResolveOpen(false);
            setSelectedId(null);
            query.refetch();
          }}
        />
      )}
    </div>
  );
}

const columns: Column<DisputeRow & { id: number }>[] = [
  {
    key: "id",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.settlementId}</span>,
    width: "min",
  },
  {
    key: "event",
    header: "Evento",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg truncate max-w-xs">{r.eventName}</div>
        <div className="text-2xs text-fg-subtle">{formatDateShort(r.eventDate)}</div>
      </div>
    ),
  },
  {
    key: "productor",
    header: "Productor",
    cell: (r) => (
      <div>
        <div className="text-xs text-fg">
          {formatApplicantName({ firstName: r.productorNombre })}
        </div>
        <div className="text-2xs text-fg-subtle break-all">{r.productorEmail ?? "—"}</div>
      </div>
    ),
  },
  {
    key: "amount",
    header: "Monto en disputa",
    cell: (r) => <span className="text-fg font-semibold tabular-nums">{fmtCLP(r.amountToTransfer)}</span>,
    align: "right",
    width: "min",
  },
  {
    key: "disputedAt",
    header: "Abierta",
    cell: (r) => (
      <span className="text-fg-subtle text-xs">{formatDateTime(r.disputedAt)}</span>
    ),
    width: "min",
    align: "right",
  },
  {
    key: "status",
    header: "Estado",
    cell: (r) => (
      <StatusBadge
        status={r.status}
        tone={settlementStatusTone(r.status)}
        label={SETTLEMENT_STATUS_LABELS[r.status]}
      />
    ),
    width: "min",
  },
];

// ── Modal de resolución ──────────────────────────────────────────────
function ResolveDisputeModal({
  settlementId,
  onClose,
  onResolved,
}: {
  settlementId: number;
  onClose: () => void;
  onResolved: () => void;
}) {
  const qc = useQueryClient();
  const { push } = useToast();

  const detailQuery = useQuery({
    queryKey: ["settlements", "full", settlementId],
    queryFn: () => settlementsApi.fullDetail(settlementId),
  });

  const openDispute = useMemo<SettlementDispute | undefined>(
    () =>
      detailQuery.data?.disputes.find(
        (d) => d.statusDisputa === "abierta" || d.statusDisputa === "en_revision_mixcore",
      ),
    [detailQuery.data],
  );

  const [statusDisputa, setStatusDisputa] = useState<DisputeStatus | "">("");
  const [minuta, setMinuta] = useState("");
  const [montoAjuste, setMontoAjuste] = useState("0");
  const [confirmLarge, setConfirmLarge] = useState(false);
  const [largeWarning, setLargeWarning] = useState<{
    ratio: number;
    threshold: number;
    grossRevenue: number;
    ajusteAbsoluto: number;
  } | null>(null);

  const resolve = useMutation({
    mutationFn: () =>
      settlementsApi.resolveDispute(settlementId, {
        disputeId: openDispute!.id,
        statusDisputa: statusDisputa as DisputeStatus,
        minutaResolucion: minuta,
        montoAjuste: Number(montoAjuste) || 0,
        confirmLargeAdjustment: confirmLarge,
      }),
    onSuccess: () => {
      push({ kind: "success", message: "Disputa resuelta. Email enviado al productor." });
      qc.invalidateQueries({ queryKey: ["settlements"] });
      onResolved();
    },
    onError: (err: any) => {
      const resp = err?.response?.data;
      if (resp?.error === "ADJUSTMENT_REQUIRES_CONFIRMATION") {
        setLargeWarning({
          ratio: resp.adjustmentRatio,
          threshold: resp.threshold,
          grossRevenue: resp.grossRevenue,
          ajusteAbsoluto: resp.ajusteAbsoluto,
        });
        push({ kind: "info", message: "El ajuste es grande — confirmá el checkbox abajo." });
        return;
      }
      push({ kind: "error", message: resp?.error || "Error al resolver." });
    },
  });

  const detail: FullDetailResponse | undefined = detailQuery.data;
  const proposedAmount = detail
    ? Number(detail.settlement.amountToTransfer) + Number(montoAjuste || 0)
    : 0;
  const canSubmit =
    !!statusDisputa &&
    minuta.trim().length >= 100 &&
    !isNaN(Number(montoAjuste)) &&
    proposedAmount >= 0 &&
    (!largeWarning || confirmLarge);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60] animate-fade-in" onClick={onClose} aria-hidden />
      <div className="fixed inset-0 z-[61] flex items-center justify-center p-4 pointer-events-none">
        <div className="nx-card pointer-events-auto w-full max-w-2xl p-5 animate-fade-in flex flex-col max-h-[92vh]">
          <header className="flex items-center justify-between mb-3">
            <h3 className="nx-h2">⚖ Resolver disputa</h3>
            <button onClick={onClose} className="nx-btn-ghost p-1">
              <X size={14} />
            </button>
          </header>

          {detailQuery.isLoading || !openDispute || !detail ? (
            <div className="flex items-center justify-center py-12 text-fg-subtle">
              <Loader2 size={16} className="animate-spin mr-2" />
              Cargando…
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto pr-1 flex flex-col gap-4">
              {/* Contexto de la disputa */}
              <section className="bg-bg-subtle border border-border-subtle rounded-md p-3">
                <div className="text-2xs text-fg-subtle mb-1">
                  Disputa #{openDispute.id} · {DISPUTE_TYPE_LABELS[openDispute.tipoDisputa]} ·{" "}
                  {formatApplicantName(openDispute.createdByUser)}
                </div>
                <div className="text-xs text-fg whitespace-pre-wrap">{openDispute.motivoDisputa}</div>
                {openDispute.expectedAmount !== null && (
                  <div className="text-2xs text-fg-subtle mt-1">
                    Monto esperado por productor:{" "}
                    <strong className="text-fg">{fmtCLP(openDispute.expectedAmount)}</strong>
                  </div>
                )}
                <div className="text-2xs text-fg-subtle mt-1">
                  Monto actual del settlement:{" "}
                  <strong className="text-fg">{fmtCLP(detail.settlement.amountToTransfer)}</strong>
                </div>
              </section>

              <div>
                <label className="nx-eyebrow block mb-1">Resolución *</label>
                <select
                  value={statusDisputa}
                  onChange={(e) => setStatusDisputa(e.target.value as DisputeStatus)}
                  className="nx-input"
                >
                  <option value="">— Seleccionar —</option>
                  <option value="resuelta_a_favor_productor">
                    {DISPUTE_STATUS_LABELS.resuelta_a_favor_productor}
                  </option>
                  <option value="resuelta_a_favor_mixcore">
                    {DISPUTE_STATUS_LABELS.resuelta_a_favor_mixcore}
                  </option>
                  <option value="resuelta_acuerdo">{DISPUTE_STATUS_LABELS.resuelta_acuerdo}</option>
                </select>
              </div>

              <div>
                <label className="nx-eyebrow block mb-1">
                  Minuta de resolución <span className="text-fg-subtle normal-case">(mín 100 chars)</span> ·{" "}
                  <span className={minuta.length < 100 ? "text-warning" : "text-success"}>
                    {minuta.length}
                  </span>
                </label>
                <textarea
                  value={minuta}
                  onChange={(e) => setMinuta(e.target.value)}
                  rows={5}
                  placeholder="Detalle de la resolución, evidencia revisada, justificación del ajuste…"
                  className="nx-input resize-y text-xs"
                />
              </div>

              <div>
                <label className="nx-eyebrow block mb-1">
                  Monto de ajuste ($CLP){" "}
                  <span className="text-fg-subtle normal-case">
                    (+ favor productor / − favor Mixcore / 0 sin ajuste)
                  </span>
                </label>
                <input
                  type="number"
                  value={montoAjuste}
                  onChange={(e) => {
                    setMontoAjuste(e.target.value);
                    setLargeWarning(null);
                    setConfirmLarge(false);
                  }}
                  className="nx-input font-mono text-right"
                />
                <div className="text-2xs text-fg-subtle mt-1">
                  Nuevo amountToTransfer:{" "}
                  <strong className={proposedAmount < 0 ? "text-danger" : "text-fg"}>
                    {fmtCLP(proposedAmount)}
                  </strong>
                  {proposedAmount < 0 && (
                    <span className="text-danger ml-2">(no puede quedar negativo)</span>
                  )}
                </div>
              </div>

              {largeWarning && (
                <div className="p-3 rounded-md border border-warning/40 bg-warning-soft">
                  <div className="text-xs text-fg mb-2">
                    El ajuste representa el{" "}
                    <strong>{(largeWarning.ratio * 100).toFixed(1)}%</strong> del bruto del
                    evento ({fmtCLP(largeWarning.grossRevenue)}). Umbral configurado:{" "}
                    {(largeWarning.threshold * 100).toFixed(0)}%.
                  </div>
                  <label className="flex items-center gap-2 text-xs text-fg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={confirmLarge}
                      onChange={(e) => setConfirmLarge(e.target.checked)}
                    />
                    Confirmar este ajuste grande
                  </label>
                </div>
              )}
            </div>
          )}

          <footer className="flex justify-end gap-2 mt-4 pt-3 border-t border-border">
            <button onClick={onClose} className="nx-btn-secondary" disabled={resolve.isPending}>
              Cancelar
            </button>
            <button
              onClick={() => resolve.mutate()}
              disabled={!canSubmit || resolve.isPending}
              className="nx-btn-primary"
            >
              {resolve.isPending ? <Loader2 size={14} className="animate-spin" /> : <Gavel size={14} />}
              Resolver
            </button>
          </footer>
        </div>
      </div>
    </>
  );
}
